<template>
    <div class="ProjectsList">
        <AppTable
            ref="table"
            :columns="cols"
            :data="normalizeRows"
            :total="total"
            :loading="loading"
            hideHeader
            :rowsPerPage="pagination.rowsPerPage"
            :page="pagination.page"
            rowKey="uuid"
            class="Table"
            @table-request="handleFetchData"
        >
            <template #content="{ row }">
                <q-card
                    bordered
                    class="ProjectCard"
                    :class="{ 'cursor-pointer': !row.deleteDate }"
                    @click="handleGoToProject(row)"
                >
                    <q-card-section class="ProjectCardHeader bg-primary text-white">
                        <div class="flex row-stretch-center no-wrap">
                            <q-icon
                                name="work"
                                size="22px"
                                color="white"
                                class="q-mr-sm"
                            />
                            <div class="ProjectCardTitle ellipsis">
                                {{ row.name }}
                            </div>
                            <q-btn
                                icon="more_vert"
                                color="white"
                                dense
                                flat
                                @click.stop="openMenu(row, $event)"
                            />
                        </div>
                    </q-card-section>

                    <q-card-section class="ProjectCardBody bg-white">
                        <div class="ProjectCardRow">
                            <div class="text-caption">
                                {{ $t('general.createdAt') }}
                            </div>
                            <div class="text-body2">
                                {{ App.dateLocationFormat(row.createDate, 'long') }}
                            </div>
                        </div>

                        <div class="ProjectCardRow">
                            <div class="text-caption">
                                {{ $t('general.updateDate') }}
                            </div>
                            <div class="text-body2">
                                {{ row.tst ? App.dateLocationFormat(row.tst, 'long') : '-' }}
                            </div>
                        </div>

                        <div class="ProjectCardRow">
                            <div class="text-caption">
                                {{ $t('general.datasetCount') }}
                            </div>
                            <div class="text-body2">
                                {{ row.datasetCount }}
                            </div>
                        </div>

                        <div class="ProjectCardRow">
                            <div class="text-caption">
                                {{ $t('general.description') }}
                            </div>
                            <div class="text-body2">
                                <span class="ellipsis">{{ row.description || '-' }}</span>
                                <QTooltip
                                    v-if="row.description"
                                    maxWidth="300px"
                                >
                                    {{ row.description }}
                                </QTooltip>
                            </div>
                        </div>
                    </q-card-section>
                </q-card>
            </template>
            <template #empty>
                <EmptyList
                    v-bind="emptyMessage"
                    class="mt-2 mb-2"
                >
                    <template #footer>
                        <QBtn
                            class="mt-1"
                            color="primary"
                            noCaps
                            :label="$t('projects.new')"
                            icon="add_circle_outline"
                            @click="$emit('onNewItem')"
                        />
                    </template>
                </EmptyList>
            </template>
        </AppTable>

        <q-menu
            v-if="menuActivator"
            v-model="menuOpen"
            :target="menuActivator"
            @hide="cleanSelected"
        >
            <q-list dense class="text-caption">
                <q-item
                    v-for="option in menuOptions"
                    :key="option.key"
                    clickable
                    @click="handleClickOption(option)"
                >
                    <q-item-section avatar>
                        <q-icon :name="option.icon" :color="option.iconColor" />
                    </q-item-section>
                    <q-item-section>
                        {{ option.label }}
                    </q-item-section>
                </q-item>
            </q-list>
        </q-menu>
    </div>
</template>

<script>
/* eslint-disable func-names */
import VueTypes from 'vue-types'
import { Table as AppTable, EmptyList } from '@/components'
import { filtersMixin } from '@/mixins'

const ACTIONS = {
    DELETE: 'DELETE',
    EDIT: 'EDIT',
}

export default {
    name: 'ProjectsList',
    components: {
        AppTable,
        EmptyList,
    },
    mixins: [filtersMixin],
    inject: ['App'],
    props: {
        columns: VueTypes.array,
        rows: VueTypes.array,
        total: VueTypes.number,
        search: VueTypes.string,
        loading: VueTypes.bool.def(false),
        pagination: VueTypes.shape({
            page: VueTypes.number,
            rowsPerPage: VueTypes.number,
            sortBy: VueTypes.string,
            sortDirection: VueTypes.string,
        }).isRequired,
    },
    emits: ['onNewItem', 'onClick', 'onFetchData', 'onUpdate', 'onDelete'],
    data() {
        return {
            selected: undefined,
            menuOpen: false,
            menuActivator: null,
            menuOptions: [],
            sortableColumns: ['NAME', 'UPDATED_AT'],
            sortBy: localStorage.getItem('projectsListSortBy'),
            direction: localStorage.getItem('projectsListSortDirection'),
        }
    },
    computed: {
        cols() {
            return [
                ...this.columns,
                {
                    name: 'NAME',
                    label: this.$t('general.project'),
                    align: 'left',
                },
                {
                    name: 'description',
                    label: this.$t('general.description'),
                    align: 'left',
                },
                {
                    name: 'UPDATED_AT',
                    label: this.$t('general.updateDate'),
                    align: 'left',
                },
                {
                    name: 'datasetCount',
                    label: this.$t('general.datasetCount'),
                    align: 'left',
                    sortable: false,
                },
                {
                    name: 'actions',
                    label: '',
                },
            ]
        },
        emptyMessage() {
            return {
                title: this.$t(`projects.notifications.${this.search ? 'noResult.title' : 'empty.title'}`),
                text: this.$t(`projects.notifications.${this.search ? 'noResult.text' : 'empty.text'}`),
            }
        },
        normalizeRows() {
            return (this.rows || []).map((row) => ({
                ...row,
                options: this.generateOptionsRow(row),
            }))
        },
    },
    watch: {
        rows() {
            if (this.menuOpen) this.cleanSelected()
        },
    },
    methods: {
        generateOptionsRow({ deleteDate }) {
            const isArchived = !!deleteDate
            return [
                ...(!isArchived
                    ? [
                        {
                            key: ACTIONS.EDIT, icon: 'edit', iconColor: 'primary', label: this.$t('actions.edit'), event: 'onUpdate',
                        },
                        {
                            key: ACTIONS.DELETE, icon: 'delete', iconColor: 'primary', label: this.$t('actions.delete'), event: 'onDelete',
                        },
                    ]
                    : []),
            ]
        },
        openMenu(row, event) {
            this.selected = row
            this.menuActivator = event?.currentTarget || event?.target || null
            this.menuOptions = row?.options || []

            if (!this.menuActivator) return
            this.menuOpen = true
        },
        cleanSelected() {
            this.menuOpen = false
            this.selected = undefined
            this.menuActivator = null
            this.menuOptions = []
        },
        isSortable(colName) {
            return this.sortableColumns.includes(colName)
        },
        handleClickOption({ event }) {
            if (event) this.$emit(event, this.selected)
            this.cleanSelected()
        },
        handleGoToProject(project) {
            if (!project?.deleteDate) this.$emit('onClick', project.uuid)
        },
        updateValues(col) {
            const newDirection = this.sortBy === col.name && this.direction === 'ASCENDING'
                ? 'DESCENDING'
                : 'ASCENDING'

            this.sortBy = col.name
            this.direction = newDirection
            localStorage.setItem('projectsListSortBy', this.sortBy)
            localStorage.setItem('projectsListSortDirection', this.direction)

            this.$emit('onFetchData', {
                ...this.pagination,
                sortBy: this.sortBy,
                sortDirection: this.direction,
            })
        },
        handleFetchData(props) {
            this.$emit('onFetchData', {
                ...this.pagination,
                page: props.page,
                rowsPerPage: props.rowsPerPage,
            })
        },
    },
}
</script>
<style scoped lang="scss">
.Table:deep() {
    border: none !important;

    .q-table__middle {
        overflow: visible;
    }

    .q-table__bottom {
        margin-top: 20px;
        border: 1px solid $table-border-color;
    }

    tbody {
        display: grid;
        grid-template-columns: repeat(1, minmax(0, 1fr));
        grid-gap: 16px;
        padding: 2px 2px 10px;
        overflow: visible;

        @media screen and (min-width: $md) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @media screen and (min-width: $xl) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        @media screen and (min-width: $xxxl) {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }
    }
}

.ProjectCard {
    width: 100%;
    height: 100%;
}

.ProjectCardHeader {
    padding: 12px 16px;
}

.ProjectCardTitle {
    font-weight: 500;
}

.ellipsis {
    display: inline-block;
    vertical-align: middle;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ProjectCardBody {
    padding: 12px 16px;
}

.ProjectCardRow {
    & + & {
        margin-top: 8px;
    }
}
</style>
